/** Formatação pt-BR. Valores monetários nunca são arredondados para exibição:
 *  o sistema inteiro se apoia em conferências que fecham centavo a centavo. */

const MOEDA = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const NUMERO = new Intl.NumberFormat('pt-BR');

export const brl = (v) => (v === null || v === undefined || v === '' ? '—' : MOEDA.format(Number(v)));

export const numero = (v) => (v === null || v === undefined ? '—' : NUMERO.format(Number(v)));

export const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

export const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * Razão pago/cobrado como percentual — é assim que um deflator sistemático
 * fica legível (0,933333 vira "93,3%").
 *
 * Uma casa decimal de propósito. Com quatro, o mesmo padrão aparecia repartido
 * em várias linhas — 133,3333%, 133,3313% e 133,3301% são a fração 4/3 com
 * ruído de centavo no arredondamento de cada guia, e mostrar as três separadas
 * escondia justamente o que a tela existe para revelar.
 */
export const percentual = (v) =>
  v === null || v === undefined ? '—' : `${(Number(v) * 100).toFixed(1).replace('.', ',')}%`;

/* ── Competência (MM/AAAA) ───────────────────────────────────────────────── */

/**
 * Máscara progressiva: o usuário digita só números e a barra entra sozinha.
 * "052026" vira "05/2026". Apagar funciona normalmente, porque a máscara é
 * recalculada a partir dos dígitos, não do texto anterior.
 */
export function mascararCompetencia(valor) {
  const bruto = String(valor || '');

  // Se a barra já está lá (digitada ou colada), ela delimita o mês: "5/2026"
  // vira "05/2026", e não "52/026" como faria o agrupamento cego por dígitos.
  if (bruto.includes('/')) {
    const [antes = '', depois = ''] = bruto.split('/');
    const mes = antes.replace(/\D/g, '').slice(0, 2);
    const ano = depois.replace(/\D/g, '').slice(0, 4);
    const mesCheio = mes.length === 1 ? `0${mes}` : mes;
    return ano ? `${mesCheio}/${ano}` : `${mesCheio}/`;
  }

  const digitos = bruto.replace(/\D/g, '').slice(0, 6);
  if (digitos.length <= 2) return digitos;
  return `${digitos.slice(0, 2)}/${digitos.slice(2)}`;
}

/** MM/AAAA com mês entre 01 e 12 e ano plausível. */
export function competenciaValida(valor) {
  const v = String(valor || '').trim();
  if (!/^\d{2}\/\d{4}$/.test(v)) return false;
  const [mes, ano] = v.split('/').map(Number);
  return mes >= 1 && mes <= 12 && ano >= 2000 && ano <= 2100;
}

/**
 * Mensagem de erro específica — dizer "formato inválido" não ajuda quem digitou
 * o mês 13 por engano.
 */
export function erroCompetencia(valor) {
  const v = String(valor || '').trim();
  if (!v) return '';
  const digitos = v.replace(/\D/g, '');
  if (digitos.length < 6) return 'Informe mês e ano — por exemplo 05/2026.';
  if (!/^\d{2}\/\d{4}$/.test(v)) return 'Use o formato MM/AAAA.';
  const [mes, ano] = v.split('/').map(Number);
  if (mes < 1 || mes > 12) return `Mês inválido: ${String(mes).padStart(2, '0')}. Use de 01 a 12.`;
  if (ano < 2000 || ano > 2100) return `Ano fora do intervalo esperado: ${ano}.`;
  return '';
}

export const competenciaAtual = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

/**
 * Máscara de dinheiro conforme se digita: "2500" vira "2.500".
 *
 * **Não é a máscara de centavos** (aquela em que "2500" vira "25,00"). Em
 * ERP ela é comum, mas aqui a pessoa está copiando um valor de um ofício
 * ou de um acordo — digita "2500" querendo dois mil e quinhentos, e uma
 * máscara que devolve vinte e cinco reais erra por 100× num campo de baixa
 * judicial. O separador decimal continua sendo a vírgula, digitada por quem
 * precisa dela.
 */
export function mascararMoeda(valor) {
  const bruto = String(valor ?? '').replace(/[^\d,]/g, '');
  if (!bruto) return '';

  const [inteiroCru = '', ...resto] = bruto.split(',');
  // Só a primeira vírgula conta; as demais são digitação repetida.
  const temVirgula = resto.length > 0;
  const decimais = resto.join('').slice(0, 2);

  const inteiro = inteiroCru.replace(/^0+(?=\d)/, '') || '0';
  const agrupado = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return temVirgula ? `${agrupado},${decimais}` : agrupado;
}

/** "2.500,50" → 2500.5. Devolve `null` quando não há número nenhum. */
export function moedaParaNumero(valor) {
  const limpo = String(valor ?? '').replace(/\./g, '').replace(',', '.');
  if (!limpo.trim()) return null;
  const n = Number(limpo);
  return Number.isFinite(n) ? n : null;
}

/** Completa os centavos ao sair do campo: "2.500" → "2.500,00". */
export function normalizarMoeda(valor) {
  const n = moedaParaNumero(valor);
  if (n === null) return '';
  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
